import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import {
  Input,
  OnInit,
  OnDestroy,
  Component,
  HostBinding,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';

import { SnComponentUpdater } from '@lib/core';

import { SnSmartButtonGroupEventType } from '../../enums/smart-button-group-event-type.enum';
import { SnSmartButton } from '../../models/smart-button.model';
import { SnSmartButtonGroupService } from '../../services/smart-button-group.service';
import { SnSmartButtonGroupEvent } from '../../types/smart-button-group-event';

@Component({
  selector: 'sn-smart-button-group',
  exportAs: 'snSmartButtonGroup',
  templateUrl: './smart-button-group.component.html',
  styleUrls: ['./smart-button-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'sn-smart-button-group' },
})
export class SnSmartButtonGroupComponent implements OnInit, OnDestroy {
  @Input() set name(value: string) {
    if (this._name !== value) {
      this._name = value;

      const buttonGroup = this._smartButtonGroupServece.getGroup(this._name);
      this.buttons = buttonGroup.buttons;
      this.hidden = !buttonGroup.isVisible;
    }
  }

  @HostBinding('class.sn-smart-button-group_hidden')
  public hidden = false;

  public buttons: SnSmartButton[] = [];

  private _name: string;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _router: Router,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _componentUpdater: SnComponentUpdater<SnSmartButtonGroupEvent>,
    private readonly _smartButtonGroupServece: SnSmartButtonGroupService,
  ) {}

  @HostBinding('class.sn-smart-button-group_empty')
  get isEmpty(): boolean {
    return !this.buttons.length;
  }

  public ngOnInit(): void {
    this._componentUpdater
      .componentUpdated$('sn-smart-button-group')
      .pipe(
        filter(({ name }) => this._name === name),
        takeUntil(this._destroy$),
      )
      .subscribe((event) => {
        switch (event.type) {
          case SnSmartButtonGroupEventType.Update:
            this.buttons = event.buttons;
            break;

          case SnSmartButtonGroupEventType.Visibility:
            this.hidden = !event.visible;
            break;
        }

        this._changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackByIdFn(index: number, button: SnSmartButton): string {
    return button.name;
  }

  public onButtonClick(event: MouseEvent, button: SnSmartButton): void {
    if (button.disabled) {
      return;
    }

    if (button.click) {
      button.click(event);
    }

    if (button.url) {
      this._router.navigateByUrl(button.url);
    }
  }
}
